import React, { useEffect, useState } from "react";
import NotificationTemplateTable from "./NotificationTemplateTable";
import userServices from "services/userServices";
import { useNotification } from "providers/NotificationProvider";
import NotificationTemplatePreviewModal from "./NotificationTemplatePreviewModal";
import { useTranslation } from "context/TranslationProvider";
import DeleteModal from "modals/DeleteModal";

function NotificationTemplate() {
  const { notify } = useNotification();
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [tableState, setTableState] = useState({
    page: 1,
    limit: 20,
    sortBy: "createdAt",
    sortOrder: -1,
    search: "",
  });

  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
  });

  const fetchTemplates = async () => {
    setIsLoading(true);

    try {
      const params = {
        page: tableState.page,
        limit: tableState.limit,
        sortBy: tableState.sortBy,
        sortOrder: tableState.sortOrder,
        search: tableState.search,
      };

      const res = await userServices.getAllTemplates(params);

      if (!res?.success) {
        notify("error", t("page.notificationTemplates.fetchFailed", "Failed to fetch templates"));
        return;
      }

      setTemplates(res.data || res.templates || []);
      setMeta(res.pagination || res.meta || { totalItems: 0, totalPages: 0 });

    } catch (error) {
      notify("error", t("page.notificationTemplates.fetchFailed", "Failed to fetch templates"));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = async (id) => {
    try {
      setPreviewHtml("");
      setIsPreviewOpen(true);

      const res = await userServices.previewEmailTemplate(id);

      if (!res?.success) {
        notify("error", t("page.notificationTemplates.toast.previewFailed", "Failed to preview email template"));
        setIsPreviewOpen(false);
        return;
      }

      setPreviewHtml(res.data?.html || res.html || "");
    } catch (error) {
      notify("error", t("page.notificationTemplates.toast.previewFailed", "Failed to preview email template"));
      setIsPreviewOpen(false);
    }
  };

  const handleDeleteClick = (template) => {
    setSelectedTemplate(template);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      const res = await userServices.deleteTemplate(selectedTemplate._id);

      if (!res || res.error) {
        notify("error", t(res?.error, res?.message || t("page.notificationTemplates.toast.deleteFailed", "Failed to delete")));
        return;
      }

      notify("success", t(res?.messageKey, res?.message || t("common.deleted", "Deleted successfully")));
      setDeleteModalOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      notify("error", t("page.notificationTemplates.toast.deleteFailed", "Failed to delete"));
    }
  };

  const handleToggleStatus = async (template, newStatus) => {
    try {
      const res = await userServices.updateTemplate(template._id, {
        isActive: newStatus,
      });

      if (!res || res.error) {
        notify("error", t(res?.error, res?.message || t("page.notificationTemplates.toast.updateFailed", "Failed to update status")));
        return;
      }

      notify("success", t(res?.messageKey, res?.message || t("common.updated", "Updated successfully")));
      fetchTemplates();
    } catch (error) {
      notify("error", t("page.notificationTemplates.toast.updateFailed", "Failed to update status"));
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line
  }, [
    tableState.page,
    tableState.limit,
    tableState.sortBy,
    tableState.sortOrder,
    tableState.search,
  ]);

  return (
    <>
      <NotificationTemplateTable
        templates={templates}
        isLoading={isLoading}
        tableState={tableState}
        setTableState={setTableState}
        meta={meta}
        onPreview={handlePreview}
        onToggleStatus={handleToggleStatus}
        onDeleteClick={handleDeleteClick}
      />

      <NotificationTemplatePreviewModal
        isOpen={isPreviewOpen}
        toggle={() => setIsPreviewOpen(false)}
        htmlContent={previewHtml}
      />

      <DeleteModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        itemName="default"
        onDelete={handleConfirmDelete}
      />
    </>
  );
}

export default NotificationTemplate;